<template>
  <section
    class="relative w-full overflow-hidden bg-gradient-to-r from-gray-50/10 via-gray-50/70 to-gray-100/10"
  >
    <KfContainer
      size="lg"
      class="relative z-10 pt-16 overflow-visible"
    >
      <div class="grid grid-cols-4 gap-6">
        <!-- LEFT: drives height -->
        <div class="col-span-2">
          <div>
            <h2
              v-if="footerTitle"
              class="text-3xl md:text-4xl font-semibold mb-10 text-black"
            >
              {{ footerTitle }}
            </h2>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div class="flex flex-col gap-2 justify-center items-start z-10">
                <KfLink
                  :href="footerStartChatLink.href"
                  type="button-secondary"
                  class="!w-full !border-gray-100 !text-primary-bravo hover:!text-white !border !justify-between flex flex-row !bg-gray-50 lg:!bg-transparent hover:!bg-primary-bravo"
                >
                  <span>{{ footerStartChatLink.buttonLabel }}</span>
                  <span><CustomerChat /></span>
                </KfLink>
                <small class="pl-5 ">{{
                  footerStartChatLink.labelUnderButton
                }}</small>
              </div>

              <!-- CONTACT US WITH DOT -->
              <div class="flex flex-col gap-2 justify-center items-start">
                <KfLink
                  :href="footerContactUsLink.href"
                  type="button-secondary"
                  class="!w-full !border-gray-100 !text-primary-bravo hover:!text-white !border !justify-between flex flex-row !bg-gray-50 lg:!bg-transparent hover:!bg-primary-bravo items-center"
                >
                  <span class="flex items-center gap-2">
                    {{ footerContactUsLink.buttonLabel }}
                  </span>
                  <span><CustomerCall /></span>
                </KfLink>

                <small class="pl-5  flex justify-center items-center">{{
                  footerContactUsLink.labelUnderButton
                }} <span
                  v-if="isOpenNow"
                  class="inline-block ml-2 w-2 h-2 mt-1 rounded-full bg-success animate-pulse"
                  aria-label="Open now"
                /></small>
              </div>

              <div class="flex flex-col gap-2 justify-center items-start">
                <KfLink
                  :href="footerContactFormLink.href"
                  type="button-secondary"
                  class="!w-full !border-gray-100 !text-primary-bravo hover:!text-white !border !justify-between flex flex-row !bg-gray-50 lg:!bg-transparent hover:!bg-primary-bravo"
                >
                  <span class="text-left">{{ footerContactFormLink.buttonLabel }}</span>
                  <span><CustomerForm /></span>
                </KfLink>
                <small class="pl-5 ">{{
                  footerContactFormLink.labelUnderButton
                }}</small>
              </div>
              <div class="flex flex-col gap-2 justify-center items-start">
                <KfLink
                  :href="footerFaqLink.href"
                  type="button-secondary"
                  class="!w-full !border-gray-100 !text-primary-bravo hover:!text-white !border !justify-between flex flex-row !bg-gray-50 lg:!bg-transparent hover:!bg-primary-bravo"
                >
                  <span class="text-left">{{ footerFaqLink.buttonLabel }}</span>
                  <span><CustomerFaq /></span>
                </KfLink>
                <small class="pl-5 ">{{ footerFaqLink.labelUnderButton }}</small>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT: empty spacer so grid stays 2-cols but doesn't set height -->
        <div class="relative pointer-events-none col-span-2">
          <!-- Image (independent of grid height) -->
          <img
            :src="footerImageUrl"
            alt="Footer Customer Service Image"
            class=" -z-10 select-none absolute  -left-80 lg:left-0 h-[160%] lg:h-[250%] -top-0 md:-top-10 lg:-top-36 object-cover object-left overflow-visible"
          >
        </div>
      </div>
    </KfContainer>

    <!-- bottom curve -->
    <kf-curve
      curve-alignment="left"
      curve-svg-color="gray"
      class="relative z-10 -mb-1"
    />
  </section>
</template>

<script>
import KfCurve from '@/components/base/curve/Curve.vue';
import KfLink from '@/components/base/link/Link.vue';
import KfContainer from '@/components/layout/container/Container.vue';
import CustomerCall from '@/components/svg/CustomerCall.vue';
import CustomerChat from '@/components/svg/CustomerChat.vue';
import CustomerFaq from '@/components/svg/CustomerFaq.vue';
import CustomerForm from '@/components/svg/CustomerForm.vue';

const TZ = 'Europe/Amsterdam';

// Vanilla helper to get a Date object representing "now" in the given TZ
function nowInZone(zone) {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(new Date()).reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
  return new Date(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
    0,
  );
}

export default {
  name: 'FooterCustomerSupport',
  components: {
    KfContainer,
    KfLink,
    KfCurve,
    CustomerChat,
    CustomerCall,
    CustomerForm,
    CustomerFaq,
  },
  props: {
    footerTitle: {
      type: String,
      default: '',
    },
    footerContactFormLink: {
      type: Object,
      default: () => ({}),
    },
    footerContactUsLink: {
      type: Object,
      default: () => ({}),
    },
    footerFaqLink: {
      type: Object,
      default: () => ({}),
    },
    footerImageUrl: {
      type: String,
      default: null,
    },
    footerStartChatLink: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isOpenNow: false,
      timer: null,
    };
  },
  created() {
    this.updateOpenState();
    this.timer = setInterval(this.updateOpenState, 60 * 1000); // every minute
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
  methods: {
    updateOpenState() {
      const now = nowInZone(TZ);
      const weekday = now.getDay(); // 0 Sun ... 6 Sat
      const isWeekday = weekday >= 1 && weekday <= 5;

      const open = new Date(now);
      open.setHours(8, 30, 0, 0);

      const close = new Date(now);
      close.setHours(17, 0, 0, 0);

      this.isOpenNow = isWeekday && now >= open && now <= close;
    },
  },
};
</script>
